"use client";

import type { CapacityFields } from "@/utils/publicEventCapacity";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useCallback, useContext } from "react";

type LiveCapacity = {
  capacity: CapacityFields;
  /** False until a `fetchOnMount` provider has its first live answer. */
  settled: boolean;
  setCapacity: (capacity: CapacityFields) => void;
};

const LiveCapacityContext = createContext<LiveCapacity | null>(null);

const capacityKey = (eventId: string) => ["event-capacity", eventId];

async function fetchCapacity(eventId: string): Promise<CapacityFields> {
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH ?? "";
  const res = await fetch(
    `${basePath}/api/events/${encodeURIComponent(eventId)}/capacity`,
  );
  if (!res.ok) {
    // An unread body keeps the request open, so the page never goes network-idle.
    await res.body?.cancel();
    throw new Error(`Capacity request failed: ${res.status}`);
  }
  return res.json();
}

/**
 * Capacity for one event, starting from the server-rendered value. With
 * `fetchOnMount`, that value is treated as stale and replaced from the live
 * capacity route, because the page HTML may come from the edge cache.
 */
export function LiveCapacityProvider({
  eventId,
  initial,
  fetchOnMount,
  children,
}: {
  eventId: string;
  initial: CapacityFields;
  fetchOnMount: boolean;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const { data, isFetchedAfterMount } = useQuery({
    queryKey: capacityKey(eventId),
    queryFn: () => fetchCapacity(eventId),
    initialData: initial,
    initialDataUpdatedAt: fetchOnMount ? 0 : undefined,
    enabled: fetchOnMount,
    staleTime: 10_000,
    refetchOnWindowFocus: true,
  });
  const setCapacity = useCallback(
    (capacity: CapacityFields) =>
      queryClient.setQueryData(capacityKey(eventId), capacity),
    [queryClient, eventId],
  );

  return (
    <LiveCapacityContext.Provider
      value={{
        capacity: data,
        settled: !fetchOnMount || isFetchedAfterMount,
        setCapacity,
      }}
    >
      {children}
    </LiveCapacityContext.Provider>
  );
}

/** The live capacity, or `fallback` outside a {@link LiveCapacityProvider}. */
export function useLiveCapacity(fallback: CapacityFields): CapacityFields {
  return useContext(LiveCapacityContext)?.capacity ?? fallback;
}

/** Whether the live capacity has replaced a possibly stale initial value. */
export function useLiveCapacitySettled(): boolean {
  return useContext(LiveCapacityContext)?.settled ?? true;
}

const noop = () => {};

/** Replaces the live capacity; does nothing outside a provider. */
export function useSetLiveCapacity(): (capacity: CapacityFields) => void {
  return useContext(LiveCapacityContext)?.setCapacity ?? noop;
}
